/**
 * Cancelled-item spec (R2): cancel / uncancel a billable item.
 *
 * Cancelling runs the read-only `cancel-impact` pre-check first, then routes to
 * one of three outcomes (matching AC2/AC4/AC5):
 *   - blocked (on a sent/partially_paid/paid/overdue invoice) → block dialog,
 *     no mutation. The user must deal with that invoice first.
 *   - removable (on draft/ready invoices only) → confirm dialog naming the
 *     invoice(s) it will be removed from + that they'll be recalculated.
 *   - unattached → light confirm.
 * On confirm we PUT { cancelled: true }; the server is authoritative and
 * re-checks impact, so we defensively handle a 409 by showing the block view.
 *
 * Uncancelling (AC6) is a simple confirm — no pre-check, no invoice warnings.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/api';
import { useSetBillableItemCancelled } from '@/hooks';
import type { BillableItem } from '@/lib/schemas';

interface InvoiceRef {
  invoice_number: string;
  status: string;
}

interface CancelBillableItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: BillableItem;
  /** Called after a successful cancel/uncancel mutation. */
  onComplete?: () => void;
}

function InvoiceList({ invoices }: { invoices: InvoiceRef[] }) {
  return (
    <ul className="mt-2 space-y-1">
      {invoices.map((inv) => (
        <li key={inv.invoice_number} className="text-sm">
          <span className="font-medium">#{inv.invoice_number}</span>{' '}
          <span className="text-muted-foreground">({inv.status})</span>
        </li>
      ))}
    </ul>
  );
}

export function CancelBillableItemDialog({
  open,
  onOpenChange,
  item,
  onComplete,
}: CancelBillableItemDialogProps) {
  const isCancelling = !item.cancelled; // false→true = cancel; true→false = uncancel
  const setCancelled = useSetBillableItemCancelled();

  // Defensive 409: if the server rejects the PUT after a clean pre-check, we
  // capture the blocking invoices here and flip to the block view.
  const [conflictInvoices, setConflictInvoices] = useState<InvoiceRef[] | null>(null);

  // Reset transient conflict state whenever the dialog (re)opens.
  useEffect(() => {
    if (open) setConflictInvoices(null);
  }, [open]);

  // Pre-check only runs when opening in CANCEL mode (uncancel has no impact).
  const {
    data: impact,
    isLoading: impactLoading,
    isError: impactError,
  } = useQuery({
    queryKey: ['billable-items', item.id, 'cancel-impact'],
    queryFn: () => api.billableItems.cancelImpact(item.id),
    enabled: open && isCancelling,
    gcTime: 0,
    staleTime: 0,
  });

  const handleConfirmCancel = async () => {
    try {
      await setCancelled.mutateAsync({ id: item.id, cancelled: true });
      onComplete?.();
      onOpenChange(false);
    } catch (error: any) {
      // State changed between pre-check and PUT — show the block view.
      // This app's exception handler (main.py) serializes HTTPException.detail
      // under the JSON key `error`, so the real path is error.data.error.blocking_invoices.
      // Keep detail/flat fallbacks for safety.
      if (error?.status === 409) {
        setConflictInvoices(
          error?.data?.error?.blocking_invoices ??
            error?.data?.detail?.blocking_invoices ??
            error?.data?.blocking_invoices ??
            []
        );
        return;
      }
      // Other errors are toasted by the hook; close the dialog.
      onOpenChange(false);
    }
  };

  const handleConfirmUncancel = async () => {
    try {
      await setCancelled.mutateAsync({ id: item.id, cancelled: false });
      onComplete?.();
    } catch {
      // toasted by the hook
    } finally {
      onOpenChange(false);
    }
  };

  // ---- Uncancel mode (AC6) ------------------------------------------------
  if (!isCancelling) {
    return (
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore this billable item?</AlertDialogTitle>
            <AlertDialogDescription>
              This makes the item eligible to be invoiced again. No invoices are changed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={setCancelled.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-uncancel-item"
              onClick={(e) => {
                e.preventDefault();
                handleConfirmUncancel();
              }}
              disabled={setCancelled.isPending}
            >
              {setCancelled.isPending ? 'Restoring...' : 'Restore Item'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // ---- Cancel mode --------------------------------------------------------
  const blocked = conflictInvoices !== null || impact?.blocked === true;
  const blockingInvoices: InvoiceRef[] = conflictInvoices ?? impact?.blocking_invoices ?? [];
  const removableInvoices: InvoiceRef[] = impact?.removable_invoices ?? [];

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        {impactLoading ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel billable item</AlertDialogTitle>
              <AlertDialogDescription>Checking invoices…</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Close</AlertDialogCancel>
            </AlertDialogFooter>
          </>
        ) : blocked ? (
          // AC2 — block: cannot cancel; on sent invoice(s).
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Can't cancel this item</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <p>
                    It's on the following invoice(s) that have already been sent. Update or
                    remove it from those invoices first, then cancel it.
                  </p>
                  <InvoiceList invoices={blockingInvoices} />
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Close</AlertDialogCancel>
            </AlertDialogFooter>
          </>
        ) : impactError ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Couldn't check this item</AlertDialogTitle>
              <AlertDialogDescription>
                We couldn't load this item's invoice impact. Please try again.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Close</AlertDialogCancel>
            </AlertDialogFooter>
          </>
        ) : removableInvoices.length > 0 ? (
          // AC4 — confirm with removal: names the draft/ready invoice(s).
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel this item?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  <p>
                    This item is on the following draft/ready invoice(s). Cancelling will
                    remove it from them and recalculate their totals.
                  </p>
                  <InvoiceList invoices={removableInvoices} />
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={setCancelled.isPending}>Keep Item</AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-cancel-item"
                onClick={(e) => {
                  e.preventDefault();
                  handleConfirmCancel();
                }}
                disabled={setCancelled.isPending}
              >
                {setCancelled.isPending ? 'Cancelling...' : 'Cancel Item & Remove'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : (
          // AC5 — unattached: light confirm.
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel this item?</AlertDialogTitle>
              <AlertDialogDescription>
                It will be marked cancelled and excluded from invoicing. You can restore it
                later.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={setCancelled.isPending}>Keep Item</AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-cancel-item"
                onClick={(e) => {
                  e.preventDefault();
                  handleConfirmCancel();
                }}
                disabled={setCancelled.isPending}
              >
                {setCancelled.isPending ? 'Cancelling...' : 'Cancel Item'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}
